import { useSyncExternalStore } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslation } from 'next-i18next/pages';
import { Link } from '@ev-tech/ui-core-components/Link';
import { IconButton } from '@ev-tech/ui-core-components/IconButton';

import { ArrowLeftIcon } from '@/icons/small/ArrowLeft';
import { TRANSLATION_NAMESPACE } from '@/i18n/config';
import { useExposeTracking } from '@/hooks/useExposeTracking';
import { useExposePreviousUrlStore } from '@/hooks/useExposePreviousUrlStore';

const TRUSTED_REFERRER_DOMAINS = ['engelvoelkers.com', 'envdevel.com', 'envstage.com'] as const;
const VERCEL_REFERRER_DOMAIN_SUFFIX = 'new-search.vercel.app';

const NOOP = () => null;

const isTrustedReferrerUrl = (url: string): boolean => {
  try {
    const { hostname } = new URL(url);

    return (
      hostname === 'localhost' ||
      TRUSTED_REFERRER_DOMAINS.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`)) ||
      hostname.endsWith(VERCEL_REFERRER_DOMAIN_SUFFIX)
    );
  } catch {
    return false;
  }
};

const getBrowserBackTargetUrl = (previousUrl: string | null) => {
  if (typeof window === 'undefined') return null;

  // oxlint-disable-next-line typescript/no-unnecessary-condition
  const canGoBack = window.navigation?.canGoBack ?? window.history.length > 1;

  if (!canGoBack) return null;

  return [previousUrl, document.referrer].find((url): url is string => Boolean(url && isTrustedReferrerUrl(url))) ?? null;
};

type BackButtonProps = {
  className?: string;
  shouldShowLabel?: boolean;
  testId?: string;
};

export const BackButton = ({ className, shouldShowLabel, testId = 'expose-back-button' }: BackButtonProps) => {
  const router = useRouter();
  const { previousUrl, clearPreviousUrl } = useExposePreviousUrlStore();
  const { trackExposeNavigationEvent } = useExposeTracking();
  const { t } = useTranslation(TRANSLATION_NAMESPACE.property_expose);

  // safely return null during SSR & hydration, then read browser-only history/referrer state after hydration.
  const backTargetUrl = useSyncExternalStore(
    () => NOOP,
    () => getBrowserBackTargetUrl(previousUrl),
    NOOP,
  );

  if (!backTargetUrl) return null;

  const label = t('header.navigation.button.back');

  const handleClick = () => {
    trackExposeNavigationEvent({ targetUrl: backTargetUrl, elementId: 'navigate-back_link' });
    clearPreviousUrl();
    router.back();
  };

  return shouldShowLabel ? (
    <Link
      as="button"
      data-testid={testId}
      onClick={handleClick}
      className={className}
      icon={<ArrowLeftIcon />}
      iconPosition="left"
      label={label}
    />
  ) : (
    <IconButton
      data-testid={testId}
      className={className}
      label={label}
      icon={<ArrowLeftIcon />}
      onClick={handleClick}
      size="medium"
      variant="primaryAlt"
    />
  );
};
